// VRP data import/export functions
import { v4 as uuidv4 } from 'uuid';

// To avoid circular imports, directly define LocationType constant
const LocationType = {
  NORMAL: 'normal',
  START: 'start',
  END: 'end',
  WAYPOINT: 'waypoint'
};


export const exportLocationsToJson = (locations, filename = "locations.json") => {
  try {
    // Setup export data
    const data_to_export = {
      locations: locations.map(loc => ({
        id: loc.id,
        name: loc.name,
        latitude: loc.latitude,
        longitude: loc.longitude,
        type: loc.type // keep the point type
      })),
      exportTime: new Date().toISOString()
    };
    
    // Convert to JSON string
    const json_str = JSON.stringify(data_to_export, null, 2);
    
    // Create Blob object
    const blob = new Blob([json_str], { type: "application/json" });
    
    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Release URL object
    URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Failed to export path point data:', error);
    throw error;
  }
};

export const exportLocationsToCsv = (locations, filename = "locations.csv") => {
  try {
    // Create CSV header
    const headers = ['id', 'name', 'latitude', 'longitude', 'type'];
    const csvContent = [
      headers.join(','),
      ...locations.map(loc => [
        loc.id,
        `"${loc.name}"`, // quotes for commas
        loc.latitude,
        loc.longitude,
        loc.type
      ].join(','))
    ].join('\n');
    
    // Create Blob object
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    
    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Release URL object
    URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Failed to export CSV data:', error);
    throw error;
  }
};

export const exportLocationsToGeoJson = (locations, filename = "locations.geojson") => {
  try {
    // Create GeoJSON FeatureCollection
    const geojson = {
      type: "FeatureCollection",
      crs: {
        type: "name",
        properties: {
          name: "urn:ogc:def:crs:OGC:1.3:CRS84"
        }
      },
      features: locations.map(loc => ({
        type: "Feature",
        properties: {
          id: loc.id,
          name: loc.name,
          type: loc.type,
          exported_from: "VRP_Module",
          export_time: new Date().toISOString()
        },
        geometry: {
          type: "Point",
          coordinates: [parseFloat(loc.longitude), parseFloat(loc.latitude)]
        }
      }))
    };
    
    // Convert to JSON string
    const jsonString = JSON.stringify(geojson, null, 2);
    
    // Create Blob object
    const blob = new Blob([jsonString], { type: "application/geo+json" });
    
    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Release URL object
    URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Failed to export GeoJSON data:', error);
    throw error;
  }
};

export const importLocationsFromJson = (file) => {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const jsonData = JSON.parse(event.target.result);
          
          // Validate data format
          if (!jsonData.locations || !Array.isArray(jsonData.locations)) {
            throw new Error('Invalid JSON format, missing locations array');
          }
          
          // Validate each location point object
          const validatedLocations = jsonData.locations.map((loc, index) => {
            // Check required fields
            if (!loc.latitude || !loc.longitude) {
              throw new Error(`Location point ${index+1} missing required coordinate information`);
            }
            
            // Return normalized location object
            // Preserve original ID and type, use defaults if not present
            return {
              id: loc.id || uuidv4(),
              name: loc.name || `Point ${index+1}`,
              latitude: parseFloat(loc.latitude),
              longitude: parseFloat(loc.longitude),
              type: loc.type || LocationType.NORMAL // Preserve original type or use default type
            };
          });
          
          resolve(validatedLocations);
        } catch (parseError) {
          reject(parseError);
        }
      };
      
      reader.onerror = () => {
        reject(new Error('File read failed'));
      };
      
      reader.readAsText(file);
    } catch (error) {
      reject(error);
    }
  });
};


export const importLocationsFromGeoJson = (file) => {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const geojsonData = JSON.parse(event.target.result);
          
          // Validate GeoJSON format
          if (geojsonData.type !== 'FeatureCollection') {
            throw new Error('Invalid GeoJSON format, must be a FeatureCollection');
          }
          
          const features = geojsonData.features || [];
          if (!features.length) {
            throw new Error('GeoJSON file contains no features');
          }
          
          // Convert GeoJSON features to location points
          const validatedLocations = features.map((feature, index) => {
            // Validate feature structure
            if (feature.type !== 'Feature') {
              throw new Error(`Feature ${index+1} is not a valid Feature`);
            }
            
            const geometry = feature.geometry || {};
            if (geometry.type !== 'Point') {
              throw new Error(`Feature ${index+1} is not a Point geometry`);
            }
            
            const coordinates = geometry.coordinates || [];
            if (coordinates.length < 2) {
              throw new Error(`Feature ${index+1} has invalid coordinates`);
            }
            
            const [longitude, latitude] = coordinates;
            const properties = feature.properties || {};
            
            // Generate name from properties
            let name = properties.name || 
                      properties.title || 
                      properties.label ||
                      properties.operator ||
                      `Point ${index+1}`;
            
            // If name contains address info, use it
            if (!properties.name && properties['addr:street']) {
              const addrParts = [];
              if (properties['addr:housenumber']) addrParts.push(properties['addr:housenumber']);
              if (properties['addr:street']) addrParts.push(properties['addr:street']);
              if (properties['addr:suburb']) addrParts.push(properties['addr:suburb']);
              if (addrParts.length > 0) name = addrParts.join(' ');
            }
            
            return {
              id: properties.id || properties.osm_id || uuidv4(),
              name: name,
              latitude: parseFloat(latitude),
              longitude: parseFloat(longitude),
              type: LocationType.NORMAL
            };
          });
          
          resolve(validatedLocations);
        } catch (parseError) {
          reject(parseError);
        }
      };
      
      reader.onerror = () => {
        reject(new Error('File read failed'));
      };
      
      reader.readAsText(file);
    } catch (error) {
      reject(error);
    }
  });
};

export const importLocationsFromCsv = (file) => {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const csvText = event.target.result;
          const lines = csvText.split('\n').filter(line => line.trim());
          
          if (lines.length < 2) {
            throw new Error('CSV file must contain at least a header row and one data row');
          }
          
          // Parse header
          const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
          
          // Find coordinate columns (support multiple naming conventions)
          const latIndex = headers.findIndex(h => 
            h.includes('lat') || h.includes('y') || h === 'latitude'
          );
          const lngIndex = headers.findIndex(h => 
            h.includes('lng') || h.includes('lon') || h.includes('x') || h === 'longitude'
          );
          
          if (latIndex === -1 || lngIndex === -1) {
            throw new Error('CSV file must contain latitude and longitude columns (lat/latitude, lng/lon/longitude)');
          }
          
          // Find optional columns
          const nameIndex = headers.findIndex(h => 
            h.includes('name') || h.includes('title') || h.includes('label')
          );
          const idIndex = headers.findIndex(h => 
            h.includes('id') || h === 'identifier'
          );
          const typeIndex = headers.findIndex(h => 
            h.includes('type') || h.includes('category')
          );
          
          // Parse data rows
          const validatedLocations = [];
          for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(',').map(v => v.trim());
            
            if (values.length < headers.length) {
              console.warn(`Row ${i+1} has fewer columns than header, skipping`);
              continue;
            }
            
            const latitude = parseFloat(values[latIndex]);
            const longitude = parseFloat(values[lngIndex]);
            
            if (isNaN(latitude) || isNaN(longitude)) {
              console.warn(`Row ${i+1} has invalid coordinates, skipping`);
              continue;
            }
            
            const location = {
              id: (idIndex >= 0 && values[idIndex]) ? values[idIndex] : uuidv4(),
              name: (nameIndex >= 0 && values[nameIndex]) ? values[nameIndex] : `Point ${i}`,
              latitude: latitude,
              longitude: longitude,
              type: (typeIndex >= 0 && values[typeIndex]) ? values[typeIndex] : LocationType.NORMAL
            };
            
            validatedLocations.push(location);
          }
          
          if (validatedLocations.length === 0) {
            throw new Error('No valid location points found in CSV file');
          }
          
          resolve(validatedLocations);
        } catch (parseError) {
          reject(parseError);
        }
      };
      
      reader.onerror = () => {
        reject(new Error('File read failed'));
      };
      
      reader.readAsText(file);
    } catch (error) {
      reject(error);
    }
  });
};

export const importLocationsFromFile = (file) => {
  const fileName = file.name.toLowerCase();
  const fileType = file.type.toLowerCase();
  
  // Determine file format
  if (fileName.endsWith('.geojson') || fileName.endsWith('.json') && fileType.includes('geo')) {
    return importLocationsFromGeoJson(file);
  } else if (fileName.endsWith('.csv') || fileType.includes('csv')) {
    return importLocationsFromCsv(file);
  } else if (fileName.endsWith('.json') || fileType.includes('json')) {
    return importLocationsFromJson(file);
  } else {
    return Promise.reject(new Error('Unsupported file format. Please upload JSON, GeoJSON, or CSV files.'));
  }
};

export const createPathGeoJson = (path, locations, vehiclePaths = null) => {
  try {
    // Standard GeoJSON format structure
    const geojson = {
      type: 'FeatureCollection',
      features: [],
      properties: {
        is_vrp: Boolean(vehiclePaths && vehiclePaths.length > 1),
        vehicle_count: vehiclePaths ? vehiclePaths.length : 1,
        path_ids: path // Save original path ID sequence
      }
    };
    
    // If there is multi-vehicle path data, create a Feature for each vehicle
    if (vehiclePaths && Array.isArray(vehiclePaths) && vehiclePaths.length > 0) {

      // Create a LineString Feature for each vehicle
      vehiclePaths.forEach((vehicle, idx) => {
        if (!vehicle.path || vehicle.path.length < 2) return;
        
        // Extract path coordinates
        const coordinates = vehicle.path.map(point => {
          // Handle different point formats
          if (typeof point === 'string') {
            // If point is an ID, look up in locations
            const loc = locations.find(l => l.id === point);
            return loc ? [loc.longitude, loc.latitude] : null;
          } else if (typeof point === 'object') {
            // If point is an object
            if (point.id) {
              // If it has an ID, look up in locations
              const loc = locations.find(l => l.id === point.id);
              return loc ? [loc.longitude, loc.latitude] : null;
            } else if (point.longitude || point.lng) {
              // If it has direct coordinates
              return [point.longitude || point.lng, point.latitude || point.lat];
            }
          }
          return null;
        }).filter(Boolean); // Filter out null values
        
        // Only create Feature if there are enough coordinate points
        if (coordinates.length >= 2) {
          // Extract point IDs from path
          const pathIds = vehicle.path.map(point => {
            if (typeof point === 'string') return point;
            return point.id || null;
          }).filter(Boolean);
          
          // Create Feature
          geojson.features.push({
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: coordinates
            },
            properties: {
              vehicle_id: vehicle.id || vehicle.vehicle_id || idx + 1,
              color: vehicle.color || '#2196F3', // Default blue
              distance: vehicle.distance || 0,
              path_ids: pathIds // Save point ID sequence
            }
          });
        }
      });
    } else if (path && path.length >= 2) {
      // If no multi-vehicle paths, create single path Feature

      // Extract path coordinates
      const coordinates = path.map(id => {
        const loc = locations.find(l => l.id === id);
        return loc ? [loc.longitude, loc.latitude] : null;
      }).filter(Boolean);
      
      // Only create Feature if there are enough coordinate points
      if (coordinates.length >= 2) {
        // Create Feature
        geojson.features.push({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: coordinates
          },
          properties: {
            vehicle_id: 1,
            color: '#FF5722', // Default orange
            path_ids: path // Save point ID sequence
          }
        });
      }
    }
    
    return geojson;
  } catch (error) {
    console.error('Failed to create path GeoJSON format data:', error);
    return {
      type: 'FeatureCollection',
      features: [],
      properties: { error: error.message }
    };
  }
};

export const exportPathToGeoJson = (path, locations, vehiclePaths = null, filename = "path.geojson") => {
  try {
    // Create GeoJSON format path data
    const geojson = createPathGeoJson(path, locations, vehiclePaths);
    
    // Convert to JSON string
    const json_str = JSON.stringify(geojson, null, 2);
    
    // Create Blob object
    const blob = new Blob([json_str], { type: "application/json" });
    
    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Release URL object
    URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Failed to export path GeoJSON data:', error);
    throw error;
  }
}; 